/**
 * Approve adjustment — preview and send informational provider email (no action links).
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { Close, Send, Email } from "@mui/icons-material";
import { previewAdjustmentProviderEmail, approveAdjustment } from "../services/adjustmentService.js";
import { formatMoney } from "../utils/formatMoney.js";
import { logError } from "../../../shared/utils/logger.js";

export default function AdjustmentApproveModal({
  open,
  onClose,
  jobId,
  adjustment,
  approvedBy,
  onApproved,
}) {
  const [emailContent, setEmailContent] = useState("");
  const [providerEmail, setProviderEmail] = useState(null);
  const [providerName, setProviderName] = useState(null);
  const [emailFound, setEmailFound] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const needsProviderEmail = ["PROVIDER", "SHARED"].includes(adjustment?.responsibility);

  const loadPreview = useCallback(async () => {
    if (!jobId || !adjustment?.id || !needsProviderEmail) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const result = await previewAdjustmentProviderEmail(jobId, adjustment.id);
      if (result.success) {
        setEmailContent(result.data.emailContent || "");
        setProviderEmail(result.data.providerEmail || null);
        setProviderName(result.data.providerName || null);
        setEmailFound(!!result.data.providerEmail);
      } else {
        setError(result.error || "Failed to load provider email preview");
      }
    } catch (err) {
      setError(err.message || "Failed to load preview");
      logError("Adjustment provider preview", { error: err.message });
    } finally {
      setPreviewLoading(false);
    }
  }, [jobId, adjustment?.id, needsProviderEmail]);

  useEffect(() => {
    if (open && adjustment && needsProviderEmail) {
      loadPreview();
    }
    if (open && adjustment && !needsProviderEmail) {
      setEmailContent("");
      setError(null);
    }
  }, [open, adjustment?.id, needsProviderEmail, loadPreview]);

  const handleApprove = async () => {
    setError(null);
    if (needsProviderEmail && (!emailContent.trim() || !emailFound)) {
      setError("Provider email preview is required before approval.");
      return;
    }

    setLoading(true);
    const res = await approveAdjustment(jobId, adjustment.id, approvedBy, {
      emailContent: needsProviderEmail ? emailContent.trim() : undefined,
    });
    setLoading(false);

    if (res.success) {
      onApproved?.(res);
      handleClose();
    } else {
      setError(res.error || "Failed to approve adjustment");
    }
  };

  const handleClose = () => {
    if (loading) return;
    setError(null);
    onClose?.();
  };

  if (!adjustment) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Approve adjustment</Typography>
          <IconButton onClick={handleClose} size="small" disabled={loading}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          {needsProviderEmail
            ? "Approving will record partner recovery on payout and send an informational email to the service provider (no links or actions required from them)."
            : "Bubakii-only adjustment — no partner notification email."}
        </Alert>

        <Typography variant="body2" sx={{ mb: 2 }}>
          {adjustment.type?.replace(/_/g, " ")} — {formatMoney(adjustment.amount)} (
          {adjustment.responsibility})
        </Typography>

        {needsProviderEmail && (
          <>
            <Box
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: 1,
                bgcolor: emailFound ? "#e8f5e9" : "#ffebee",
                border: `1px solid ${emailFound ? "#4caf50" : "#f44336"}`,
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Email color={emailFound ? "success" : "error"} />
                <Typography variant="body2" fontWeight="bold">
                  Provider email
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {emailFound
                  ? `${providerName ? `${providerName} — ` : ""}${providerEmail}`
                  : "No email on driver/partner profile — add before approving"}
              </Typography>
            </Box>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Provider notice preview (editable)
            </Typography>
            {previewLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <TextField
                fullWidth
                multiline
                rows={14}
                value={emailContent}
                onChange={(e) => setEmailContent(e.target.value)}
                required
                helperText="Plain informational notice — no links or action requests"
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Send />}
          onClick={handleApprove}
          disabled={
            loading ||
            (needsProviderEmail && (previewLoading || !emailContent.trim() || !emailFound))
          }
          sx={{ textTransform: "none" }}
        >
          Approve & notify provider
        </Button>
      </DialogActions>
    </Dialog>
  );
}
